import { motion } from "framer-motion";
import {
  FaSpider,
  FaDatabase,
  FaChartLine,
  FaRobot,
  FaSearch,
  FaCloud,
  FaCode,
  FaShieldAlt,
} from "react-icons/fa";
import { BiData } from "react-icons/bi";
import Seo from "./Seo";

// Web scraper services data
const scraperServices = [
  {
    icon: <FaSpider className="h-10 w-10" />,
    title: "Web Data Extraction",
    description:
      "Professional web scraping services in Nepal — extract structured data from any website, eCommerce platform, or online directory at scale with high accuracy and zero manual effort.",
    color: "primary",
    technologies: ["Puppeteer", "Playwright", "Cheerio", "Node.js"],
  },
  {
    icon: <FaSearch className="h-10 w-10" />,
    title: "SERP & Keyword Scraping",
    description:
      "Track Google search rankings, scrape SERP results, and extract competitor keyword data automatically. Get real-time SEO intelligence without paying for expensive third-party tools.",
    color: "pink",
    technologies: [
      "SERP Scraping",
      "Proxy Rotation",
      "Keyword Tracking",
      "Rank Monitoring",
    ],
  },
  {
    icon: <FaChartLine className="h-10 w-10" />,
    title: "eCommerce Price Monitoring",
    description:
      "Automated price scraping across multiple eCommerce platforms in Nepal and globally. Monitor competitor pricing, track stock levels, and get instant alerts when prices change.",
    color: "primary",
    technologies: [
      "Price Tracking",
      "Stock Alerts",
      "Competitor Analysis",
      "MongoDB",
    ],
  },
  {
    icon: <FaRobot className="h-10 w-10" />,
    title: "Headless Browser Automation",
    description:
      "Scrape JavaScript-rendered pages, handle infinite scroll, bypass CAPTCHA, and automate complex user interactions using Puppeteer and Playwright — built to beat modern anti-bot systems.",
    color: "pink",
    technologies: [
      "Puppeteer",
      "Playwright",
      "Anti-bot Bypass",
      "CAPTCHA Handling",
    ],
  },
  {
    icon: <BiData className="h-10 w-10" />,
    title: "Data Pipeline & ETL",
    description:
      "End-to-end data extraction, transformation, and loading pipelines. Raw scraped data is cleaned, deduplicated, structured, and delivered in CSV, JSON, or directly into your database.",
    color: "primary",
    technologies: ["ETL Pipelines", "Data Cleaning", "MongoDB", "MySQL"],
  },
  {
    icon: <FaCloud className="h-10 w-10" />,
    title: "Scheduled & Cloud Scraping",
    description:
      "Deploy fully automated scrapers with cron job scheduling, proxy rotation, and real-time monitoring. Cloud-hosted pipelines that run 24/7 and deliver fresh data without manual intervention.",
    color: "pink",
    technologies: ["Cron Jobs", "Proxy Rotation", "Cloud Deploy", "Auto-Retry"],
  },
];

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.6,
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

const cardVariants = {
  hidden: { opacity: 0, scale: 1 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: "easeOut",
    },
  },
};

const WebScraper = () => {
  return (
    <div className="relative min-h-screen bg-dark-900/40 text-light-100 overflow-hidden">
      <Seo
        title="Web Scraping Services in Nepal | Aashish Timsina"
        description="Web scraping services from Nepal: eCommerce price monitoring, SERP tracking, lead generation and custom data extraction pipelines built with Node.js."
        path="/webscraper"
      />
      <div className="relative z-10">
        {/* Header Section */}
        <motion.section
          className="py-20 px-6"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <div className="container mx-auto text-center">
            <motion.div
              className="flex justify-center items-center mb-6"
              variants={itemVariants}
            >
              <div className="p-4 rounded-full bg-gradient-to-r from-primary-500 to-pink-500">
                <FaSpider className="h-12 w-12 text-white" />
              </div>
            </motion.div>

            <motion.span
              className="text-xs uppercase tracking-widest text-primary-400 mb-4 block"
              variants={itemVariants}
            >
              Web Scraper & Backend Developer in Nepal
            </motion.span>

            <motion.h1
              className="text-5xl md:text-6xl font-bold font-space-grotesk mb-6"
              variants={itemVariants}
            >
              <span className="gradient-text">Web Scraping</span>
              <span className="text-light-100"> Services in Nepal</span>
            </motion.h1>

            <motion.p
              className="text-xl text-light-200 max-w-3xl mx-auto leading-relaxed mb-8"
              variants={itemVariants}
            >
              I build professional web scraping solutions for businesses in
              Nepal and worldwide — from eCommerce price monitoring and SERP
              tracking to lead generation and competitor data extraction. Fast,
              reliable, and built to handle anti-bot systems at scale.
            </motion.p>

            {/* Stats row */}
            <motion.div
              className="flex flex-wrap justify-center gap-8 mt-10"
              variants={itemVariants}
            >
              {[
                { value: "3+", label: "Years Experience" },
                { value: "100+", label: "Scrapers Built" },
                { value: "99%", label: "Data Accuracy" },
                { value: "24/7", label: "Automated Runs" },
              ].map((stat, index) => (
                <div key={index} className="text-center">
                  <div className="text-3xl font-bold gradient-text">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-400 mt-1">{stat.label}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </motion.section>

        {/* Services Grid */}
        <motion.section
          className="py-16 px-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <div className="container mx-auto">
            <motion.div className="mb-12 max-w-3xl" variants={itemVariants}>
              <span className="font-code text-sm text-primary-400 mb-3 block">
                What I Build
              </span>
              <h2 className="text-4xl font-bold font-space-grotesk mb-4">
                <span className="text-primary-400">Web Scraping</span> Services
              </h2>
              <p className="text-light-200 text-lg max-w-2xl mx-auto">
                From single-page data extraction to large-scale automated
                pipelines — tailored scraping solutions for every business need
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {scraperServices.map((service, index) => (
                <motion.div
                  key={index}
                  className="group relative w-full"
                  variants={cardVariants}
                  transition={{ duration: 0.3 }}
                >
                  <div
                    className={`
                      relative p-6 md:p-8 rounded-lg border transition-all duration-300 
                      bg-gray-800/90 backdrop-blur-sm h-full shadow-xl
                      ${
                        service.color === "primary"
                          ? "border-primary-500/30 hover:border-primary-500/50 group-hover:shadow-2xl group-hover:shadow-primary-500/20"
                          : "border-pink-500/30 hover:border-pink-500/50 group-hover:shadow-2xl group-hover:shadow-pink-500/20"
                      }
                    `}
                  >
                    {/* Glow effect */}
                    <div
                      className={`
                        absolute inset-0 rounded-lg opacity-0 
                        group-hover:opacity-20 transition-opacity duration-300 
                        ${
                          service.color === "primary"
                            ? "bg-gradient-to-br from-primary-500/20 to-transparent"
                            : "bg-gradient-to-br from-pink-500/20 to-transparent"
                        }
                      `}
                    />

                    {/* Icon */}
                    <div
                      className={`
                        relative z-10 inline-flex p-3 md:p-4 rounded-xl mb-4 md:mb-6 
                        group-hover:scale-110 transition-transform duration-300
                        ${
                          service.color === "primary"
                            ? "bg-primary-500/20 text-primary-400"
                            : "bg-pink-500/20 text-pink-400"
                        }
                      `}
                    >
                      {service.icon}
                    </div>

                    {/* Content */}
                    <div className="relative z-10">
                      <h3 className="text-lg md:text-xl font-bold font-space-grotesk mb-3 md:mb-4 text-white">
                        {service.title}
                      </h3>

                      <p className="text-gray-300 text-sm md:text-base leading-relaxed mb-4 md:mb-6">
                        {service.description}
                      </p>

                      {/* Technologies */}
                      <div className="flex flex-wrap gap-2">
                        {service.technologies.map((tech, techIndex) => (
                          <span
                            key={techIndex}
                            className={`
                              px-2 md:px-3 py-1 text-xs rounded-full 
                              ${
                                service.color === "primary"
                                  ? "bg-primary-500/20 text-primary-300 border border-primary-500/30"
                                  : "bg-pink-500/20 text-pink-300 border border-pink-500/30"
                              }
                            `}
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Key Features Section */}
        <motion.section
          className="py-16 px-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <div className="container mx-auto">
            <motion.div className="mb-12 max-w-3xl" variants={itemVariants}>
              <span className="font-code text-sm text-primary-400 mb-3 block">
                Why Work With Me
              </span>
              <h2 className="text-4xl font-bold font-space-grotesk mb-4">
                Why Choose My{" "}
                <span className="text-primary-400">Scraping Solutions</span>
              </h2>
              <p className="text-light-200 text-lg max-w-2xl mx-auto">
                Built by a backend developer in Nepal who understands both the
                technical and business side of data extraction
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                {
                  icon: <FaCode className="h-8 w-8" />,
                  title: "Custom Built",
                  description:
                    "Every scraper is built from scratch for your specific target — no generic tools or one-size-fits-all templates",
                },
                {
                  icon: <FaShieldAlt className="h-8 w-8" />,
                  title: "Anti-Bot Ready",
                  description:
                    "Handles CAPTCHAs, proxy rotation, rate limiting, and JavaScript rendering to scrape even the toughest sites",
                },
                {
                  icon: <FaDatabase className="h-8 w-8" />,
                  title: "Clean Data Output",
                  description:
                    "Scraped data is validated, deduplicated, and delivered in CSV, JSON, or directly into MongoDB or MySQL",
                },
                {
                  icon: <FaCloud className="h-8 w-8" />,
                  title: "Fully Automated",
                  description:
                    "Set it and forget it — scheduled cron jobs run your scrapers 24/7 with auto-retry and failure alerts",
                },
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  className="text-center group"
                  variants={itemVariants}
                >
                  <div className="inline-flex p-4 rounded-xl mb-4 bg-gradient-to-br from-primary-500/20 to-pink-500/10 text-primary-400 group-hover:scale-110 transition-transform duration-300">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg font-bold mb-2">{feature.title}</h3>
                  <p className="text-light-200 text-sm">
                    {feature.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* Use Cases Section */}
        <motion.section
          className="py-16 px-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <div className="container mx-auto">
            <motion.div className="mb-12 max-w-3xl" variants={itemVariants}>
              <span className="font-code text-sm text-primary-400 mb-3 block">
                Common Use Cases
              </span>
              <h2 className="text-4xl font-bold font-space-grotesk mb-4">
                What Businesses Use{" "}
                <span className="text-primary-400">Web Scraping</span> For
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
              {[
                {
                  title: "eCommerce Price Intelligence",
                  description:
                    "Track competitor prices across Daraz, Amazon, and other platforms in real time to stay competitive and maximize margins.",
                },
                {
                  title: "Lead Generation",
                  description:
                    "Extract business emails, phone numbers, and contact details from directories and websites to build targeted outreach lists.",
                },
                {
                  title: "SERP Rank Monitoring",
                  description:
                    "Automatically track your keyword positions on Google and monitor competitor rankings without paying for expensive SEO tools.",
                },
                {
                  title: "Market Research & Trends",
                  description:
                    "Collect product reviews, news articles, and social data to understand market trends and customer sentiment at scale.",
                },
                {
                  title: "Real Estate & Property Data",
                  description:
                    "Scrape property listings, prices, and location data from real estate platforms for investment analysis or aggregation.",
                },
                {
                  title: "Job & Recruitment Data",
                  description:
                    "Monitor job postings across multiple platforms, track hiring trends, and collect structured recruitment data automatically.",
                },
              ].map((useCase, index) => (
                <motion.div
                  key={index}
                  className="flex items-start gap-4 p-6 rounded-lg bg-gray-800/50 border border-primary-500/10 hover:border-primary-500/30 transition-all duration-300"
                  variants={itemVariants}
                >
                  <div className="mt-1 flex-shrink-0 w-2 h-2 rounded-full bg-primary-400 mt-2"></div>
                  <div>
                    <h3 className="text-base font-bold text-white mb-2">
                      {useCase.title}
                    </h3>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {useCase.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section
          className="py-20 px-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <div className="container mx-auto text-center">
            <motion.div className="max-w-3xl mx-auto" variants={itemVariants}>
              <h2 className="text-4xl font-bold font-space-grotesk mb-6">
                Ready to{" "}
                <span className="text-primary-400">Extract Your Data?</span>
              </h2>
              <p className="text-xl text-light-200 mb-8">
                Based in Nepal and serving clients worldwide — let's discuss
                your web scraping needs and build a reliable, automated data
                extraction solution tailored to your business.
              </p>
              <motion.button
                className="px-8 py-4 bg-gradient-to-r from-primary-500 to-pink-500 text-white font-bold rounded-xl hover:shadow-lg hover:shadow-primary-500/25 transition-all duration-300"
                onClick={() => (window.location.href = "/contact")}
              >
                Start Your Project
              </motion.button>
            </motion.div>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default WebScraper;
